import React, { useEffect } from 'react';
import { loadTinyMCE } from './MedicalCertificateDialog';
import { Modal, Button } from 'antd';

type Props = {
    initialContent: string;
    onSave: (content: string, parsedContent: string) => void;
    onClose: () => void;
};

const TinyMceEditorModal: React.FC<Props> = ({ initialContent, onSave, onClose }) => {
    const modalEditorId = `modal-editor-symptoms`;

    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null;

        async function loadTinyMceScript() {
            try {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                if (tinymce) {
                    initEditor();
                } else {
                    await loadTinyMCE();
                    initEditor();
                }
            } catch (e) {
                await loadTinyMCE();
                initEditor();
            }
        }

        // Add a small delay to ensure the modal is rendered before initializing TinyMCE
        timeoutId = setTimeout(() => {
            loadTinyMceScript();
        }, 100);

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            try {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                tinymce?.get(modalEditorId)?.destroy();
            } catch (e) {
                console.error(e);
            }
        };
    }, []);

    const initEditor = () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        tinymce?.init({
            selector: `#${modalEditorId}`,
            height: '60vh',
            width: '100%',
            spellchecker_language: 'en',
            browser_spellcheck: true,
            statusbar: false,
            menubar: false,
            plugins: ['advlist', 'lists', 'paste'],
            toolbar: 'bold italic bullist',
            toolbar_location: 'top',
            toolbar_persist: false,
            content_style:
                '#tinymce { font-family:Helvetica,Arial,sans-serif; font-size:13px; margin-top: 4px; } #tinymce p, #tinymce ul{ margin: 0 0;}',
            entity_encoding: 'named',
            setup: (editor: any) => {
                editor?.on('init', function () {
                    editor?.setContent(initialContent || '');
                });
            },
        });
    };

    const handleSave = () => {
        try {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const editor = tinymce?.get(modalEditorId);

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore

            if (editor) {
                const content = editor.getContent();
                const textContent = editor.getContent({ format: 'text' });

                // First call onSave with the content
                onSave(content, textContent);

                // Then close the modal
                onClose();
            } else {
                console.error('Editor not found when trying to save');
                // Still try to close the modal even if we couldn't save
                onClose();
            }
        } catch (error) {
            console.error(' Error saving content:', error);
            // Still try to close the modal even if there was an error
            onClose();
        }
    };

    return (
        <Modal
            title="Edit Content"
            visible={true}
            onCancel={onClose}
            width="80%"
            style={{ top: 20 }}
            bodyStyle={{ padding: '12px', height: '70vh', overflow: 'hidden' }}
            destroyOnClose={true}
            maskClosable={false}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button key="save" type="primary" onClick={handleSave}>
                    Save
                </Button>,
            ]}
        >
            <div style={{ height: '100%' }}>
                <div id={modalEditorId} style={{ height: '100%' }}></div>
            </div>
        </Modal>
    );
};

export default TinyMceEditorModal;
